import { redirect } from "@tanstack/react-router";
import Cookies from "js-cookie";

import { Route, routes, token } from "../constants";
import { BusinessType } from "../services/queries/businesses/types";
import { UserRole } from "../services/queries/users/types";
import { getUserRoleFromToken } from "./user";
import { matchRouteByPathname, AppRoutes } from "./index";

/**
 * Single access predicate shared by the sidebar (nav visibility) and
 * route guards (enforcement). Hiding a link is UX; this is the policy.
 */
export function canAccessRoute(
  route: Route,
  businessType?: BusinessType,
  userRole?: UserRole,
): boolean {
  if (!route.isAppRoute) return true;

  if (route.businessType && businessType) {
    if (!route.businessType.includes(businessType)) return false;
  }

  if (route.userRole && userRole) {
    if (!route.userRole.includes(userRole)) return false;
  }

  return true;
}

/**
 * Reads the session snapshot available outside React (beforeLoad runs
 * before the component tree renders): business from localStorage, role
 * from the JWT in the token cookie.
 */
function readSessionSnapshot(): {
  businessType: BusinessType | undefined;
  userRole: UserRole | undefined;
} {
  let businessType: BusinessType | undefined;
  try {
    const storedBusiness = localStorage.getItem("business");
    if (storedBusiness) {
      businessType = JSON.parse(storedBusiness)?.businessType;
    }
  } catch {
    // corrupted storage — treat as no business context
  }

  const authToken = Cookies.get(token);
  const userRole = authToken ? getUserRoleFromToken(authToken) : undefined;

  return { businessType, userRole };
}

/**
 * Route-level RBAC guard for `beforeLoad`. Throws a redirect to the
 * dashboard when the current session may not access the route.
 *
 * Usage in a route file:
 *   beforeLoad: () => requireRouteAccess("/flights"),
 */
export function requireRouteAccess(pathname: string): void {
  const route = matchRouteByPathname(routes, pathname);
  if (!route) return; // unknown path — let the router's notFound handle it

  const { businessType, userRole } = readSessionSnapshot();

  if (!canAccessRoute(route, businessType, userRole)) {
    throw redirect({
      to: AppRoutes.DASHBOARD,
      search: { unauthorized: route.slug },
    });
  }
}
